import React from 'react';
import { Tooltip, Typography, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { ISMEDesignGuidelinesExtended, Column } from './SMEDesignGuidelineTypes';
import { getFormattedDate } from '../../../Utils/UnitsHelper';
import { createColumnFormats } from './SMEDesignGuidelineComponents';

/**
 * Create table columns configuration
 */
export const createTableColumns = (
  onEdit: (item: ISMEDesignGuidelinesExtended) => void
): Column[] => {
  const formats = createColumnFormats();
  
  return [
    {
      id: 'industry',
      label: 'Industry',
      minWidth: 100,
      maxWidth: 120,
      format: formats.tooltipFormat
    },
    {
      id: 'subIndustry',
      label: 'Sub Industry',
      minWidth: 100,
      maxWidth: 120,
      format: formats.tooltipFormat
    },
    {
      id: 'application',
      label: 'Application',
      minWidth: 100,
      maxWidth: 120,
      format: formats.tooltipFormat
    },
    {
      id: 'subApplication',
      label: 'Sub App.',
      minWidth: 100,
      maxWidth: 120,
      format: formats.tooltipFormat
    },
    {
      id: 'processStage',
      label: 'Process Stage',
      minWidth: 100,
      maxWidth: 120,
      format: formats.tooltipFormat
    },
    {
      id: 'productLine',
      label: 'Product Line',
      minWidth: 100,
      maxWidth: 120,
      format: formats.tooltipFormat
    },
    {
      id: 'alfaLavalPosition',
      label: 'Position',
      minWidth: 100,
      maxWidth: 120,
      format: formats.tooltipFormat
    },
    {
      id: 'designSuggestion',
      label: 'Design Suggestion',
      minWidth: 140,
      maxWidth: 160,
      format: formats.tooltipFormat
    },
    {
      id: 'created',
      label: 'Created',
      minWidth: 100,
      maxWidth: 120,
      format: formats.dateFormat
    },
    {
      id: 'version',
      label: 'Version',
      minWidth: 80,
      maxWidth: 100,
      format: formats.versionFormat
    },
    {
      id: 'actions',
      label: '',
      minWidth: 60,
      maxWidth: 80,
      align: 'center' as const,
      sortable: false,
      format: (value: any, item?: ISMEDesignGuidelinesExtended) => {
        return formats.actionsFormat(value, item, onEdit);
      }
    }
  ];
};

/**
 * Validate form data before submission
 */
export const validateFormData = (
  selectedIndustry: string,
  selectedSubIndustry: string,
  selectedApplication: string,
  selectedSubApplication: string,
  selectedProcessStage: string,
  selectedProductLine: string,
  selectedPosition: string,
  designSuggestion: string
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!selectedIndustry.trim()) {
    errors.push('Industry is required');
  }
  
  if (!selectedSubIndustry.trim()) {
    errors.push('Sub Industry is required');
  }
  
  if (!selectedApplication.trim()) {
    errors.push('Application is required');
  }
  
  if (!selectedSubApplication.trim()) {
    errors.push('Sub Application is required');
  }
  
  if (!selectedProcessStage.trim()) {
    errors.push('Process Stage is required');
  }
  
  if (!selectedProductLine.trim()) {
    errors.push('Product Line is required');
  }
  
  if (!selectedPosition.trim()) {
    errors.push('Alfa Laval Position is required');
  }
  
  if (!designSuggestion.trim()) {
    errors.push('Design Suggestion is required');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Check if any filter is active
 */
export const hasActiveFilters = (filter: {
  industry: string;
  subIndustry: string;
  application: string;
  subApplication: string;
  processStage: string;
  productLine: string;
  alfaLavalPosition: string;
}): boolean => {
  return !!(
    filter.industry ||
    filter.subIndustry ||
    filter.application ||
    filter.subApplication ||
    filter.processStage ||
    filter.productLine ||
    filter.alfaLavalPosition
  );
};

/**
 * Generate unique key for table rows
 */
export const generateRowKey = (item: ISMEDesignGuidelinesExtended, index: number): string => {
  return `${item.industry}-${item.subIndustry}-${item.application}-${index}`;
};

/**
 * Format performance metrics for display
 */
export const formatPerformanceMetrics = (metrics: Record<string, number>): string => {
  return Object.entries(metrics)
    .sort(([, a], [, b]) => b - a)
    .map(([key, value]) => `${key}: ${value.toFixed(2)}ms`)
    .join('\n');
};

/**
 * Check if component should show performance metrics
 */
export const shouldShowPerformanceMetrics = (metrics: Record<string, number>): boolean => {
  return process.env.NODE_ENV === 'development' && Object.keys(metrics).length > 0;
};

/**
 * Get summary statistics for design guidelines
 */
export const getDataSummary = (
  currentData: ISMEDesignGuidelinesExtended[],
  filteredData: ISMEDesignGuidelinesExtended[]
) => {
  const totalCount = currentData.length;
  const filteredCount = filteredData.length;
  const editableCount = filteredData.filter(item => item.isEditable && item.canEdit).length;
  const nonEditableCount = filteredCount - editableCount;

  return {
    totalCount,
    filteredCount,
    editableCount,
    nonEditableCount,
    filterPercentage: totalCount > 0 ? Math.round((filteredCount / totalCount) * 100) : 0
  };
};

/**
 * Deep clone object (utility for state management)
 */
export const deepClone = <T>(obj: T): T => {
  return JSON.parse(JSON.stringify(obj));
};

/**
 * Debounce function for search/filter operations
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/**
 * Compare two arrays for equality (shallow comparison)
 */
export const arraysEqual = <T>(a: T[], b: T[]): boolean => {
  if (a.length !== b.length) return false;
  return a.every((val, index) => val === b[index]);
};

/**
 * Get unique values from array
 */
export const getUniqueValues = <T>(array: T[], key: keyof T): T[keyof T][] => {
  return Array.from(new Set(array.map(item => item[key])));
};

/**
 * Sort array by multiple criteria
 */
export const multiSort = <T>(
  array: T[],
  comparers: Array<(a: T, b: T) => number>
): T[] => {
  return array.sort((a, b) => {
    for (const comparer of comparers) {
      const result = comparer(a, b);
      if (result !== 0) return result;
    }
    return 0;
  });
}; 